import { create } from 'zustand';
import { api, Cat } from '../lib/api';
import { getApiErrorMessage, isValidationError } from '../lib/errors';
import i18n from '../i18n';

// ============================================================================
// localStorage helpers — persist the selected-cat ID so it survives
// error-boundary recovery cycles and full page reloads.
//
// IMPORTANT: the persisted key is scoped by the authenticated user's ID. This
// prevents one user's selected-cat ID (or stale in-memory currentCat) from
// bleeding into another user's session when accounts are switched in the same
// browser without a full page reload (DEF: data isolation bug where a
// newly-signed-up user could see the previous account's cat on the dashboard).
// ============================================================================

function getCurrentUserId(): string | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string };
    return parsed.id ?? null;
  } catch {
    return null;
  }
}

function currentCatKey(): string | null {
  const userId = getCurrentUserId();
  return userId ? `ckd_currentCatId:${userId}` : null;
}

function persistCatId(id: string | null) {
  try {
    const key = currentCatKey();
    if (!key) return;
    if (id) {
      localStorage.setItem(key, id);
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // localStorage unavailable — silently degrade
  }
}

function loadPersistedCatId(): string | null {
  try {
    const key = currentCatKey();
    if (!key) return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Remove every per-user selected-cat key from localStorage. Called on logout
 * to make sure stale per-user keys don't accumulate forever in a shared
 * browser, and that the next signup doesn't accidentally pick up a previous
 * tenant's value if they happen to share an id (shouldn't, but be defensive).
 */
function clearAllPersistedCatIds() {
  try {
    // Old, un-scoped key used by earlier builds — clear it too.
    localStorage.removeItem('ckd_currentCatId');
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith('ckd_currentCatId:')) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // ignore
  }
}

// ============================================================================
// Types
// ============================================================================

interface CatState {
  currentCat: Cat | null;
  cats: Cat[];
  isLoading: boolean;
  error: string | null;
  /** Set when `error` is the backend's own reason (a validation or conflict
   *  response): the raw text, for "Technical details". Null for failures with no
   *  reason to show — a server fault or a lost connection (finding 16). */
  errorTechnical: string | null;
}

interface CatActions {
  fetchCats: () => Promise<void>;
  /** Reset the store to its initial empty state. Called on logout to prevent
   *  one user's data from leaking into another user's session. */
  reset: () => void;
  createCat: (data: {
    name: string;
    sex?: 'male' | 'female' | null;
    breed?: string | null;
    date_of_birth?: string | null;
    weight_at_diagnosis: number | null;
    /** Scale the diagnosis weight was taken on — required with a weight. */
    diagnosis_weight_method?: 'home_scale' | 'vet_scale' | null;
    diagnosis_date: string;
    diagnosis_date_precision?: string;
    initial_iris_stage: number | null;
    comorbidities?: unknown[];
    initial_lab_results?: {
      creatinine: number;
      creatinine_unit: string;
      sdma?: number | null;
      bun?: number | null;
      bun_unit?: string;
      phosphorus?: number | null;
      phosphorus_unit?: string;
      upc_ratio?: number | null;
      systolic_bp?: number | null;
      potassium?: number | null;
      date?: string;
    };
  }) => Promise<Cat>;
  updateCat: (
    catId: string,
    data: Partial<{
      name: string;
      breed: string | null;
      date_of_birth: string | null;
      weight_at_diagnosis: number | null;
      diagnosis_weight_method: 'home_scale' | 'vet_scale' | null;
      diagnosis_date: string;
      diagnosis_date_precision: 'exact' | 'month';
      initial_iris_stage: number | null;
      current_iris_stage: number | null;
      comorbidities: unknown[];
    }>
  ) => Promise<Cat>;
  /** Archive a cat's record (F-08). Stops every reminder; deletes nothing. */
  archiveCat: (catId: string) => Promise<Cat>;
  /** Restore an archived record. Reminders resume. */
  unarchiveCat: (catId: string) => Promise<Cat>;
  setCurrentCat: (cat: Cat | null) => void;
  clearError: () => void;
}

type CatStore = CatState & CatActions;

/** The raw text behind an error whose message is the backend's own reason. */
function technicalDetail(error: unknown): string | null {
  if (isValidationError(error)) return error.technical;
  // A 409 (e.g. a duplicate name) carries the backend's sentence as its message.
  const status = (error as { status?: number } | null)?.status;
  if (status === 409 && error instanceof Error) return `409: ${error.message}`;
  return null;
}

// ============================================================================
// Store
// ============================================================================

export const useCatStore = create<CatStore>((set) => ({
  // ========================================================================
  // State
  // ========================================================================

  currentCat: null,
  cats: [],
  isLoading: true,  // Start true so OnboardingCheck waits for fetchCats() before deciding
  error: null,
  errorTechnical: null,

  // ========================================================================
  // Actions
  // ========================================================================

  async fetchCats() {
    set({ isLoading: true, error: null });
    try {
      const cats = await api.getCats();

      // Restore previously-selected cat from localStorage (survives
      // error-boundary recovery and page reloads). The persisted key is
      // user-scoped, so we'll only ever get back an id that belongs to
      // the current account.
      // With nothing persisted, open on the first ACTIVE cat (F-08): a household
      // with an archived cat and a living one should land on the living one. A
      // persisted id is still honoured even when it points at an archived record —
      // the owner selected it, and the record stays fully reachable.
      const persistedId = loadPersistedCatId();
      const firstActive = cats.find((c) => !c.archived_at) ?? cats[0] ?? null;
      const restoredCat = persistedId
        ? cats.find((c) => c.id === persistedId) ?? firstActive
        : firstActive;

      set((state) => {
        // Defensive: if the in-memory currentCat refers to a cat that the
        // server did NOT return for this user, discard it. Without this
        // check, an account switch in the same tab could leave the previous
        // user's currentCat visible on the dashboard until the next manual
        // selection — see the data-isolation bug fix.
        const currentBelongsToUser =
          state.currentCat && cats.some((c) => c.id === state.currentCat!.id);

        return {
          cats,
          currentCat: currentBelongsToUser ? state.currentCat : restoredCat,
          isLoading: false,
        };
      });
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error, i18n.t('errors.fetchCatsFailed'));
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  reset() {
    clearAllPersistedCatIds();
    set({
      currentCat: null,
      cats: [],
      isLoading: false,
      error: null,
      errorTechnical: null,
    });
  },

  async createCat(data) {
    set({ isLoading: true, error: null });
    try {
      const newCat = await api.createCat(data);
      persistCatId(newCat.id);
      set((state) => ({
        cats: [...state.cats, newCat],
        currentCat: newCat,
        isLoading: false,
      }));
      return newCat;
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error, i18n.t('errors.createCatFailed'));
      set({
        error: errorMessage,
        errorTechnical: technicalDetail(error),
        isLoading: false,
      });
      throw error;
    }
  },

  async updateCat(catId, data) {
    set({ isLoading: true, error: null });
    try {
      const updatedCat = await api.updateCat(catId, data);
      set((state) => ({
        cats: state.cats.map((cat) => (cat.id === catId ? updatedCat : cat)),
        currentCat: state.currentCat?.id === catId ? updatedCat : state.currentCat,
        isLoading: false,
      }));
      return updatedCat;
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error, i18n.t('errors.updateCatFailed'));
      set({
        error: errorMessage,
        errorTechnical: technicalDetail(error),
        isLoading: false,
      });
      throw error;
    }
  },

  async archiveCat(catId) {
    // Archiving and restoring share updateCat's merge, but not its `isLoading`
    // flag: the profile page's Save button reads that, and flipping it from a
    // dialog would disable a control the owner isn't using.
    const updated = await api.archiveCat(catId);
    set((state) => ({
      cats: state.cats.map((cat) => (cat.id === catId ? updated : cat)),
      currentCat: state.currentCat?.id === catId ? updated : state.currentCat,
    }));
    return updated;
  },

  async unarchiveCat(catId) {
    const updated = await api.unarchiveCat(catId);
    set((state) => ({
      cats: state.cats.map((cat) => (cat.id === catId ? updated : cat)),
      currentCat: state.currentCat?.id === catId ? updated : state.currentCat,
    }));
    return updated;
  },

  setCurrentCat(cat) {
    persistCatId(cat?.id ?? null);
    set({ currentCat: cat });
  },

  clearError() {
    set({ error: null, errorTechnical: null });
  },
}));
